import {useCallback, useMemo} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

export type PaywallReason = 'role' | 'integration' | 'not_enough_credits' | 'not_enough_live_pages' | 'sso';

/**
 * Reasons no self-serve plan covers: the paywall states what is missing and sends the admin to
 * sales instead of to a checkout.
 */
const SALES_ONLY_REASONS: ReadonlySet<string> = new Set<PaywallReason>(['sso']);

/** Where "Talk to sales" goes, as on the pricing page's Enterprise plan. */
export const ENTERPRISE_SALES_URL = 'https://demo.tinct.ai/';

export type HashParams = {
  reason?: string;
  totalPages?: number;
  campaignId?: string;
};

export function parseHash(hash: string, hashKey: string): HashParams | null {
  if (!hash.startsWith(`#${hashKey}`)) return null;

  const params = new URLSearchParams(hash.slice(hashKey.length + 2));
  const totalPagesRaw = params.get('totalPages');
  return {
    reason: params.get('reason') || undefined,
    totalPages: totalPagesRaw ? parseInt(totalPagesRaw, 10) : undefined,
    campaignId: params.get('campaignId') || undefined,
  };
}

export function usePaywallHash(hashKey: string) {
  const location = useLocation();

  const hashParams = useMemo(() => {
    return parseHash(location.hash, hashKey);
  }, [location.hash, hashKey]);

  const isOpen = hashParams !== null;
  const isNotEnoughCredits = hashParams?.reason === 'not_enough_credits';
  const isNotEnoughLivePages = hashParams?.reason === 'not_enough_live_pages';
  const showPlanSummary = isNotEnoughCredits || isNotEnoughLivePages;
  const isSalesOnly = !!hashParams?.reason && SALES_ONLY_REASONS.has(hashParams.reason);

  return { hashParams, isOpen, isNotEnoughCredits, isNotEnoughLivePages, showPlanSummary, isSalesOnly };
}

export type UseOpenPaywallOptions = {
  hashKey?: string;
};

export function useOpenPaywall({ hashKey = 'paywall-pro' }: UseOpenPaywallOptions = {}) {
  const navigate = useNavigate();

  const openPaywall = useCallback(
    (reason?: PaywallReason, params?: Record<string, string>) => {
      const searchParams = new URLSearchParams();
      if (reason) searchParams.set('reason', reason);
      if (params) {
        for (const [key, value] of Object.entries(params)) {
          searchParams.set(key, value);
        }
      }
      const query = searchParams.toString();
      const hash = query ? `${hashKey}?${query}` : hashKey;
      navigate({ hash }, { replace: true });
    },
    [navigate, hashKey]
  );

  const closePaywall = useCallback(() => {
    navigate({ hash: '' }, { replace: true });
  }, [navigate]);

  return { openPaywall, closePaywall };
}
